
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { ElfScene } from "@/components/tools/scene-generator/types";

interface SceneFormProps {
  currentScene: Partial<ElfScene>;
  isEditing: boolean;
  isSubmitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onReset: () => void;
  setCurrentScene: (scene: Partial<ElfScene> | ((prev: Partial<ElfScene>) => Partial<ElfScene>)) => void;
  addItemField: () => void;
  handleItemChange: (index: number, value: string) => void;
  removeItemField: (index: number) => void;
}

const SceneForm: React.FC<SceneFormProps> = ({
  currentScene,
  isEditing,
  isSubmitting,
  onSubmit,
  onReset,
  setCurrentScene,
  addItemField,
  handleItemChange,
  removeItemField,
}) => {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="title" className="text-sm font-medium">Title <span className="text-red-500">*</span></label>
          <Input 
            id="title"
            value={currentScene.title || ""}
            onChange={e => setCurrentScene({...currentScene, title: e.target.value})}
            placeholder="Enter a catchy title for the scene"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="difficulty" className="text-sm font-medium">Difficulty</label>
            <Select 
              value={currentScene.difficulty || "Medium"}
              onValueChange={value => setCurrentScene({...currentScene, difficulty: value})}
            >
              <SelectTrigger id="difficulty">
                <SelectValue placeholder="Select difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Easy">Easy</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Hard">Hard</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label htmlFor="time" className="text-sm font-medium">Time Required</label>
            <Input 
              id="time"
              value={currentScene.time_required || ""}
              onChange={e => setCurrentScene({...currentScene, time_required: e.target.value})}
              placeholder="e.g., 15 minutes"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="description" className="text-sm font-medium">Description <span className="text-red-500">*</span></label>
        <Textarea 
          id="description"
          value={currentScene.description || ""}
          onChange={e => setCurrentScene({...currentScene, description: e.target.value})}
          placeholder="Describe the scene and what it's about"
          rows={3}
          required
        />
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <label className="text-sm font-medium">Required Items <span className="text-red-500">*</span></label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addItemField}
            className="h-7"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Item
          </Button>
        </div>
        <div className="space-y-2">
          {(currentScene.required_items || []).map((item, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={item}
                onChange={e => handleItemChange(index, e.target.value)}
                placeholder="e.g., Toilet paper roll"
              />
              {(currentScene.required_items || []).length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => removeItemField(index)}
                  className="flex-shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="instructions" className="text-sm font-medium">Setup Instructions <span className="text-red-500">*</span></label>
        <Textarea 
          id="instructions"
          value={currentScene.setup_instructions || ""}
          onChange={e => setCurrentScene({...currentScene, setup_instructions: e.target.value})}
          placeholder="Provide step-by-step instructions for setting up the scene"
          rows={5}
          required
        />
      </div>

      <div className="flex justify-end space-x-2 pt-2">
        {isEditing && (
          <Button
            type="button"
            variant="outline"
            onClick={onReset}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-elfGreen hover:bg-elfGreen-dark"
        >
          {isSubmitting ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              {isEditing ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>
              {isEditing ? "Update Scene" : "Create Scene"}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default SceneForm;
